import FormattedMessage from 'I18n/components/FormattedMessage';
import { Button } from 'foundations-components/transitional/components/Button';
import Raven from 'raven-js';
import { useMemo, useRef } from 'react';
import { UpgradeProductPropertiesFromApiName } from 'self-service-api/constants/UpgradeProductProperties';
import { productsAndAddons } from 'self-service-api/core/utilities/links';
import { useIntersectionObserver } from '../../../hooks/useIntersectionObserver';
import { useUserInfo } from '../../../hooks/useUserInfo';
import { getHubAndTierFromApiName } from '../../../utils/product';
import { getTrialUpgradePageUrl, getUpgradePageUrl } from '../../../utils/urls';
import { getTrackingProperties, track } from '../../../utils/usageTracker';
import { ErrorBoundary, ErrorBoundaryNames } from '../../ErrorBoundary';
import { jsx as _jsx } from "react/jsx-runtime";
const PricingPageRedirectButtonBase = ({
  'data-test-id': dataTestId,
  'data-selenium-id': dataSeleniumId,
  apiName,
  children,
  redirectDestination,
  trackingData,
  uiButtonProps,
  upgradeConfig,
  onClick,
  isUpgradePoint
}) => {
  var _UpgradeProductProper;
  const {
    uniqueId
  } = trackingData;
  const trackingProperties = useMemo(() => getTrackingProperties({
    apiName,
    trackingData,
    upgradeConfig,
    sourceKey: 'PRICING_PAGE_REDIRECT'
  }), [apiName, trackingData, upgradeConfig]);
  const {
    user
  } = useUserInfo();
  const upgradeLink = (_UpgradeProductProper = UpgradeProductPropertiesFromApiName[apiName]) === null || _UpgradeProductProper === void 0 ? void 0 : _UpgradeProductProper.upgradeLink;
  const internalRef = useRef(null);
  const buttonRef = (uiButtonProps === null || uiButtonProps === void 0 ? void 0 : uiButtonProps.buttonRef) || internalRef;
  useIntersectionObserver({
    targetRef: user ? buttonRef : undefined,
    onIntersect: () => {
      track('pricing-page-redirect-button', Object.assign({
        action: 'viewed'
      }, trackingProperties));
      if (isUpgradePoint) {
        track('interaction', Object.assign({
          action: 'viewed pricing page redirect'
        }, trackingProperties));
      }
    }
  });
  const getRedirectLink = () => {
    if (redirectDestination === 'trialPricingPage') {
      return getTrialUpgradePageUrl(apiName);
    }
    if (redirectDestination === 'upgradePage') {
      return trackingData.usageLimitBannerData ? getUpgradePageUrl(trackingData.uniqueId, {
        limit: trackingData.usageLimitBannerData.limit,
        value: trackingData.usageLimitBannerData.value
      }) : getUpgradePageUrl(trackingData.uniqueId);
    }
    if (upgradeLink) {
      const {
        tier
      } = getHubAndTierFromApiName(apiName);
      return upgradeLink(uniqueId, tier);
    }
    return productsAndAddons();
  };
  const handleClick = () => {
    track('pricing-page-redirect-button', Object.assign({
      action: 'clicked'
    }, trackingProperties));
    if (isUpgradePoint) {
      track('interaction', Object.assign({
        action: 'clicked upgrade point'
      }, trackingProperties));
      track('interaction', Object.assign({
        action: 'clicked pricing page redirect'
      }, trackingProperties));
    }
    onClick === null || onClick === void 0 || onClick('PRICING_PAGE_REDIRECT');
  };
  if (!upgradeLink) {
    Raven.captureException(new Error(`failed to get an upgrade link in PricingPageRedirectButton`), {
      extra: {
        apiName,
        fingerprint: ['failed-to-get-upgrade-link-in-pricing-page-redirect-button']
      }
    });
    return null;
  }
  return /*#__PURE__*/_jsx(Button, Object.assign({
    target: "_blank",
    "data-test-id": dataTestId,
    "data-selenium-id": dataSeleniumId,
    href: getRedirectLink(),
    truncate: true,
    truncateStringProps: {
      justifyContent: 'center'
    }
  }, uiButtonProps, {
    buttonRef: buttonRef,
    onClick: handleClick,
    children: children !== null && children !== void 0 ? children : /*#__PURE__*/_jsx(FormattedMessage, {
      message: "purchaseMotions.buttons.pricingPageRedirect"
    })
  }));
};
export const PricingPageRedirectButton = props => /*#__PURE__*/_jsx(ErrorBoundary, {
  boundaryName: ErrorBoundaryNames.PRICING_PAGE_REDIRECT_BUTTON,
  children: /*#__PURE__*/_jsx(PricingPageRedirectButtonBase, Object.assign({}, props))
});